import { useEffect, useState } from "react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import {
  Link as LinkIcon,
  Sparkle,
  ArrowSquareOut,
  CheckCircle,
  XCircle,
  Eye,
  MagicWand,
  Clock,
  Globe,
  PencilSimple,
  Trash,
  Broom,
} from "@phosphor-icons/react";
import EditArticleModal from "./EditArticleModal";

const CONFIDENCE_STYLES = {
  verified: "bg-emerald-50 text-emerald-800 border-emerald-300",
  generally_supported: "bg-teal-50 text-teal-800 border-teal-300",
  partially_correct: "bg-amber-50 text-amber-800 border-amber-300",
  disputed: "bg-orange-50 text-orange-800 border-orange-300",
  misleading: "bg-rose-50 text-rose-800 border-rose-300",
  false: "bg-rose-100 text-rose-900 border-rose-400",
  unable_to_verify: "bg-stone-100 text-stone-700 border-stone-300",
};

function ClaimChip({ claim }) {
  const status = (claim.verification_status || "unable_to_verify").toLowerCase();
  const style = CONFIDENCE_STYLES[status] || CONFIDENCE_STYLES.unable_to_verify;
  return (
    <div
      data-testid="s2s-claim"
      className={`rounded-lg border p-2 text-xs leading-snug ${style}`}
    >
      <div className="font-mono-tech text-[9px] tracking-[0.22em] uppercase opacity-80 mb-1">
        {status.replace(/_/g, " ")} · conf {(claim.confidence ?? 0).toFixed(2)}
      </div>
      <div>{claim.text}</div>
      {claim.notes && (
        <div className="mt-1 text-[11px] opacity-75 italic">{claim.notes}</div>
      )}
    </div>
  );
}

function PreviewCard({ preview, onPublish, onDiscard, busy }) {
  if (!preview) return null;
  const src = preview.source || {};
  const subject = preview.subject || {};
  return (
    <div
      data-testid="s2s-preview"
      className="rounded-xl border border-black/20 bg-[#FFF6E5] p-4 space-y-3 mt-3"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <Globe size={14} className="text-[#B85F2A]" />
        <span className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]/80">
          {src.platform || "web"} · {src.source_type || "article"}
        </span>
        {src.canonical_url && (
          <a
            href={src.canonical_url}
            target="_blank"
            rel="noreferrer"
            data-testid="s2s-preview-source-link"
            className="ml-auto inline-flex items-center gap-1 rounded-full border border-black/15 bg-white px-2.5 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase hover:bg-black/5"
          >
            <ArrowSquareOut size={11} weight="bold" /> Open source
          </a>
        )}
      </div>

      <div className="flex gap-3">
        {src.thumbnail && (
          <img
            src={src.thumbnail}
            alt=""
            className="w-24 h-24 object-cover rounded-lg border border-black/10"
            data-testid="s2s-preview-thumbnail"
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="font-serif-display text-lg leading-tight text-[#1B4332]">
            {src.title || "Untitled source"}
          </div>
          <div className="text-xs text-[#3A3830]/80 mt-0.5">
            {src.creator || src.provider || src.publisher || "Unknown creator"}
          </div>
          {src.description && (
            <div className="text-xs text-[#3A3830]/80 mt-1 line-clamp-3">
              {src.description}
            </div>
          )}
        </div>
      </div>

      {subject && subject.primary_subject && (
        <div className="rounded-lg bg-white border border-black/10 p-3">
          <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]/70 mb-1">
            AI Subject Intelligence
          </div>
          <div className="text-sm text-[#1B4332]">
            <b>{subject.primary_subject}</b>
            {subject.secondary_subjects?.length ? (
              <span className="text-[#3A3830]/70"> · {subject.secondary_subjects.join(" · ")}</span>
            ) : null}
          </div>
          <div className="mt-1 flex flex-wrap gap-1">
            {subject.category && (
              <span className="rounded-full bg-[#E4A15C]/30 text-[#1B4332] font-mono-tech text-[9px] tracking-[0.22em] uppercase px-2 py-0.5">
                {subject.category}
              </span>
            )}
            {(subject.tags || []).slice(0, 8).map((t) => (
              <span
                key={t}
                className="rounded-full bg-black/5 border border-black/10 text-[#1B4332] font-mono-tech text-[9px] tracking-[0.22em] uppercase px-2 py-0.5"
              >
                {t}
              </span>
            ))}
          </div>
          {subject.editor_notes && (
            <div className="mt-2 text-[11px] italic text-[#3A3830]/75">
              Editor note: {subject.editor_notes}
            </div>
          )}
        </div>
      )}

      {(subject.main_claims || []).length > 0 && (
        <div>
          <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]/70 mb-1">
            Claims extracted from source
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {(subject.main_claims || []).map((c, i) => (
              <ClaimChip key={i} claim={c} />
            ))}
          </div>
        </div>
      )}

      {preview.warning && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-xs p-2">
          {preview.warning}
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <button
          disabled={busy}
          onClick={() => onPublish(false)}
          data-testid="s2s-generate-draft"
          className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 border border-[color:var(--np-brand,#B85F2A)] bg-white text-[color:var(--np-brand,#B85F2A)] font-mono-tech text-[10px] tracking-[0.22em] uppercase hover:bg-[color:var(--np-brand,#B85F2A)] hover:text-white disabled:opacity-40"
        >
          <MagicWand size={12} weight="fill" />
          {busy ? "Composing…" : "Generate article (draft)"}
        </button>
        <button
          disabled={busy}
          onClick={() => onPublish(true)}
          data-testid="s2s-generate-publish"
          className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 border border-[#1B4332] bg-[#1B4332] text-[#E4A15C] font-mono-tech text-[10px] tracking-[0.22em] uppercase hover:bg-[#E4A15C] hover:text-[#1B4332] disabled:opacity-40"
        >
          <Sparkle size={12} weight="fill" />
          {busy ? "Composing…" : "Generate + Publish"}
        </button>
        <button
          disabled={busy}
          onClick={onDiscard}
          data-testid="s2s-discard"
          className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 border border-black/15 bg-white font-mono-tech text-[10px] tracking-[0.22em] uppercase hover:bg-black/5 disabled:opacity-40"
        >
          <XCircle size={12} weight="fill" /> Discard
        </button>
      </div>
    </div>
  );
}

function LibraryRow({ item, onPublish, onEdit, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const src = (item.sources && item.sources[0]) || {};
  const run = async (action) => {
    setBusy(true);
    try {
      const { data } = await http.post(`/nature/sources/${item.id}/publish`, { action });
      toast.success(`Article ${data.article_status || action}`);
      onPublish?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };
  const del = async () => {
    if (!window.confirm(`Permanently delete "${(item.title || item.headline || "").slice(0, 60)}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await http.delete(`/nature/sources/${item.id}`);
      toast.success("Article deleted permanently");
      onDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      data-testid={`s2s-library-${item.id}`}
      className="rounded-xl border border-black/15 bg-white p-3 flex gap-3"
    >
      {(item.media?.[0]?.url || src.thumbnail) && (
        <img
          src={item.media?.[0]?.url || src.thumbnail}
          alt=""
          className="w-20 h-20 object-cover rounded-lg border border-black/10 flex-shrink-0"
        />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 rounded-full bg-black/5 border border-black/10 text-[#1B4332]">
            {src.platform || "web"}
          </span>
          <span className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#3A3830]/70">
            {item.category}
          </span>
          {item.hide_source_attribution && (
            <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 rounded-full bg-amber-50 border border-amber-300 text-amber-800">
              attribution hidden
            </span>
          )}
          <span className="ml-auto font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#3A3830]/70">
            {item.article_status || item.moderation_status}
          </span>
        </div>
        <div className="font-serif-display text-base leading-tight text-[#1B4332] mt-1 line-clamp-2">
          {item.title || item.headline}
        </div>
        <div className="text-xs text-[#3A3830]/80 mt-0.5 line-clamp-2">
          {item.one_sentence_summary || item.summary}
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          <button
            onClick={() => onEdit(item.id)}
            data-testid={`s2s-lib-edit-${item.id}`}
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 border border-[color:var(--np-brand,#B85F2A)] bg-[color:var(--np-brand,#B85F2A)] text-white font-mono-tech text-[9px] tracking-[0.22em] uppercase hover:opacity-90"
          >
            <PencilSimple size={11} weight="fill" /> Edit
          </button>
          <button
            disabled={busy || item.article_status === "published"}
            onClick={() => run("publish")}
            data-testid={`s2s-lib-publish-${item.id}`}
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 border border-emerald-400 bg-emerald-50 text-emerald-800 font-mono-tech text-[9px] tracking-[0.22em] uppercase hover:bg-emerald-100 disabled:opacity-40"
          >
            <CheckCircle size={11} weight="fill" /> Publish
          </button>
          <button
            disabled={busy || item.article_status === "rejected"}
            onClick={() => run("reject")}
            data-testid={`s2s-lib-reject-${item.id}`}
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 border border-rose-400 bg-rose-50 text-rose-800 font-mono-tech text-[9px] tracking-[0.22em] uppercase hover:bg-rose-100 disabled:opacity-40"
          >
            <XCircle size={11} weight="fill" /> Reject
          </button>
          <a
            href={`/stories/${item.id}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 border border-black/15 bg-white font-mono-tech text-[9px] tracking-[0.22em] uppercase hover:bg-black/5"
          >
            <Eye size={11} weight="fill" /> View
          </a>
          {src.canonical_url && (
            <a
              href={src.canonical_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 border border-black/15 bg-white font-mono-tech text-[9px] tracking-[0.22em] uppercase hover:bg-black/5"
            >
              <ArrowSquareOut size={11} weight="bold" /> Source
            </a>
          )}
          <button
            disabled={busy}
            onClick={del}
            data-testid={`s2s-lib-delete-${item.id}`}
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 border border-rose-500 bg-rose-500 text-white font-mono-tech text-[9px] tracking-[0.22em] uppercase hover:bg-rose-600 disabled:opacity-40"
            title="Delete permanently"
          >
            <Trash size={11} weight="fill" /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SourceToStoryPanel() {
  const [url, setUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [manualContent, setManualContent] = useState("");
  const [depth, setDepth] = useState("standard");
  const [preview, setPreview] = useState(null);
  const [busyPreview, setBusyPreview] = useState(false);
  const [busyGen, setBusyGen] = useState(false);
  const [library, setLibrary] = useState([]);
  const [libStatus, setLibStatus] = useState("needs_review");
  const [editingId, setEditingId] = useState(null);
  const extractionBlocked = !!preview?.source?.extraction_blocked;

  const loadLibrary = async () => {
    try {
      const { data } = await http.get("/nature/sources/library", {
        params: { status: libStatus === "all" ? undefined : libStatus, limit: 20 },
      });
      setLibrary(data.articles || []);
    } catch (e) {
      // Silently ignore if not admin
    }
  };

  useEffect(() => { loadLibrary(); /* eslint-disable-next-line */ }, [libStatus]);

  const runPreview = async () => {
    if (!url.trim()) {
      toast.error("Paste a URL first");
      return;
    }
    setBusyPreview(true);
    setPreview(null);
    try {
      const { data } = await http.post("/nature/sources/analyze", {
        url: url.trim(),
        notes: notes.trim() || undefined,
        depth,
        manual_content: manualContent.trim() || undefined,
      }, { timeout: 60000 });
      setPreview(data);
      if (data.warning) {
        toast.message("Preview ready", { description: data.warning });
      } else {
        toast.success("Preview ready — review and choose Draft or Publish");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setBusyPreview(false);
    }
  };

  const runGenerate = async (autopublish) => {
    if (!url.trim()) return;
    setBusyGen(true);
    try {
      const { data } = await http.post("/nature/sources/generate-article", {
        url: url.trim(),
        notes: notes.trim() || undefined,
        depth,
        autopublish,
        manual_content: manualContent.trim() || undefined,
      }, { timeout: 120000 });
      if (data.already_exists) {
        toast.message("Already exists", {
          description: `A NaturePulse article for this source already exists: "${data.title}"`,
        });
      } else {
        toast.success(
          autopublish
            ? `Published: "${(data.title || "").slice(0, 60)}…"`
            : `Draft saved: "${(data.title || "").slice(0, 60)}…"`,
        );
        // Reset the form after successful generation
        setUrl("");
        setNotes("");
        setManualContent("");
        setPreview(null);
      }
      loadLibrary();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "extraction_blocked") {
        toast.error(
          `${detail.platform || "This platform"} blocks scraping — paste a description below and try again.`,
          { duration: 8000 },
        );
      } else {
        toast.error(
          (typeof detail === "string" ? detail : e?.message) || "Article generation failed",
        );
      }
    } finally {
      setBusyGen(false);
    }
  };

  const discard = () => {
    setPreview(null);
    toast.message("Discarded preview");
  };

  return (
    <div className="rounded-2xl border border-black/15 bg-white p-4 space-y-4" data-testid="s2s-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            Source-to-Story Engine
          </div>
          <div className="font-serif-display text-xl text-[#1B4332] mt-1">
            Found an interesting article about nature or science?
          </div>
          <div className="text-sm text-[#3A3830]/85 max-w-xl">
            Paste any URL — YouTube Short, Reel, TikTok, news article, university page, government
            report, podcast — and the AI will read it, verify the claims, and compose an original
            educational NaturePulse article that credits the original source.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
        <div className="md:col-span-6 flex items-center gap-2 rounded-full border border-black/20 bg-[#FFF6E5] px-3 py-2">
          <LinkIcon size={16} className="text-[#B85F2A] flex-shrink-0" />
          <input
            data-testid="s2s-url"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://youtube.com/shorts/… or any article, video, podcast URL"
            className="flex-1 bg-transparent outline-none text-sm text-[#1B4332] placeholder:text-[#3A3830]/50"
          />
        </div>
        <input
          data-testid="s2s-notes"
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional editor notes / angle"
          className="md:col-span-4 rounded-full border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
        />
        <select
          data-testid="s2s-depth"
          value={depth}
          onChange={(e) => setDepth(e.target.value)}
          className="md:col-span-2 rounded-full border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
        >
          <option value="quick">Quick summary</option>
          <option value="standard">Standard article</option>
          <option value="detailed">Detailed deep-dive</option>
          <option value="extensive">Extensive research</option>
        </select>
      </div>

      {/* Manual content textarea — surfaces when a platform (Facebook / Instagram /
          TikTok / X) blocks scraping so the admin can describe what the video shows. */}
      <div className={`rounded-xl border p-3 space-y-1.5 transition ${
        extractionBlocked
          ? "border-amber-400 bg-amber-50"
          : "border-black/10 bg-[#FFF6E5]/50"
      }`}>
        <label
          htmlFor="s2s-manual-content"
          className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#1B4332] flex items-center gap-2"
        >
          {extractionBlocked && <span className="text-amber-700">⚠</span>}
          Describe what the source shows
          <span className="text-[#3A3830]/60 normal-case tracking-normal">
            {extractionBlocked
              ? `— ${preview?.source?.platform || "this platform"} blocks scraping, so paste a paragraph describing the video/post`
              : "— optional; helps for image-only posts or blocked platforms"}
          </span>
        </label>
        <textarea
          id="s2s-manual-content"
          data-testid="s2s-manual-content"
          value={manualContent}
          onChange={(e) => setManualContent(e.target.value)}
          placeholder={extractionBlocked
            ? "e.g. This Facebook Reel shows a beekeeper explaining that honeycomb hexagons form naturally from warm wax and surface tension. The narrator demonstrates by heating circular cells and showing them settle into hexagonal shapes over a few minutes…"
            : "Optional: paste a short description of what the video/article covers (species, place, key claims, timestamps)"
          }
          rows={extractionBlocked ? 5 : 3}
          maxLength={8000}
          className="w-full rounded-lg border border-black/15 bg-white p-2.5 text-sm outline-none focus:border-[color:var(--np-brand,#B85F2A)] resize-y"
        />
        <div className="flex items-center justify-between text-[10px] text-[#3A3830]/70 font-mono-tech tracking-widest uppercase">
          <span>{manualContent.length} / 8000</span>
          {extractionBlocked && (
            <span className="text-amber-700">Required for this platform</span>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          disabled={busyPreview || busyGen || !url.trim()}
          onClick={runPreview}
          data-testid="s2s-preview-btn"
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 border border-[color:var(--np-brand,#B85F2A)] bg-white text-[color:var(--np-brand,#B85F2A)] font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-[color:var(--np-brand,#B85F2A)] hover:text-white disabled:opacity-40"
        >
          <Eye size={13} weight="fill" /> {busyPreview ? "Analysing…" : "Preview subject"}
        </button>
        <button
          disabled={busyPreview || busyGen || !url.trim()}
          onClick={() => runGenerate(false)}
          data-testid="s2s-draft-btn"
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 border border-black/25 bg-white text-[#1B4332] font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5 disabled:opacity-40"
        >
          <MagicWand size={13} weight="fill" /> {busyGen ? "Composing…" : "Generate draft"}
        </button>
        <button
          disabled={busyPreview || busyGen || !url.trim()}
          onClick={() => runGenerate(true)}
          data-testid="s2s-publish-btn"
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 border border-[#1B4332] bg-[#1B4332] text-[#E4A15C] font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-[#E4A15C] hover:text-[#1B4332] disabled:opacity-40"
        >
          <Sparkle size={13} weight="fill" /> {busyGen ? "Publishing…" : "Generate + Publish"}
        </button>
      </div>

      {preview && (
        <PreviewCard
          preview={preview}
          busy={busyGen}
          onPublish={runGenerate}
          onDiscard={discard}
        />
      )}

      <div className="border-t border-black/10 pt-3 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#1B4332]">
            Source Library
          </div>
          {libStatus === "rejected" && library.length > 0 && (
            <button
              onClick={async () => {
                const days = Number(window.prompt(
                  "Permanently delete rejected articles older than how many days?\n" +
                  "Type 0 to purge ALL rejected. Type 30 for the default 30-day retention.",
                  "30",
                ));
                if (Number.isNaN(days) || days < 0) return;
                if (!window.confirm(
                  days === 0
                    ? `PERMANENTLY delete ALL ${library.length} rejected articles? This cannot be undone.`
                    : `Permanently delete rejected articles older than ${days} days? This cannot be undone.`,
                )) return;
                try {
                  const { data } = await http.post("/nature/sources/purge-rejected",
                    null, { params: { older_than_days: days } });
                  toast.success(`Purged ${data.purged} rejected article${data.purged === 1 ? "" : "s"}`);
                  loadLibrary();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Purge failed");
                }
              }}
              data-testid="s2s-lib-purge"
              className="inline-flex items-center gap-1 rounded-full border border-rose-500 bg-rose-500 text-white px-2.5 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase hover:bg-rose-600"
            >
              <Broom size={11} weight="fill" /> Purge rejected
            </button>
          )}
          <div className="flex flex-wrap gap-1 ml-auto">
            {["needs_review", "published", "rejected", "all"].map((s) => (
              <button
                key={s}
                onClick={() => setLibStatus(s)}
                data-testid={`s2s-lib-tab-${s}`}
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 border font-mono-tech text-[9px] tracking-[0.22em] uppercase ${
                  libStatus === s
                    ? "bg-[#E4A15C] text-[#1B4332] border-[#E4A15C]"
                    : "border-black/15 text-[#1B4332] bg-white hover:bg-black/5"
                }`}
              >
                {s.replace("_", " ")}
              </button>
            ))}
          </div>
        </div>
        {library.length === 0 ? (
          <div className="rounded-xl border border-dashed border-black/15 p-6 text-center text-[#3A3830]/70 text-sm">
            <Clock size={20} weight="light" className="mx-auto mb-1 opacity-70" />
            No Source-to-Story articles in this bucket yet.
          </div>
        ) : (
          <div className="space-y-2">
            {library.map((item) => (
              <LibraryRow key={item.id} item={item} onPublish={loadLibrary} onEdit={setEditingId} onDeleted={loadLibrary} />
            ))}
          </div>
        )}
      </div>

      {editingId && (
        <EditArticleModal
          articleId={editingId}
          onClose={() => setEditingId(null)}
          onSaved={loadLibrary}
        />
      )}
    </div>
  );
}
